import { useState, useCallback } from 'react';
import { axiosTokenInstance } from '../utils/axiosInstance.utils';
import { apiUrls, errorMessages } from '../utils/properties.utils';
import {
  OrderReportResponse,
  ProductReportResponse,
  CustomerReportResponse,
  CustomerOutstandingReportResponse,
  DashboardSummaryResponse,
  PaymentReportResponse,
} from '../models/Management.interface';

export const useGetOrderReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<OrderReportResponse | null>(
    null
  );

  const fetchOrderReport = useCallback(
    async (params: {
      statuses: string[];
      startDate: string;
      endDate: string;
      customerId?: string;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.ORDER_REPORT_API,
          {
            params: {
              statuses: params.statuses.join(','),
              startDate: params.startDate,
              endDate: params.endDate,
              ...(params.customerId ? { customerId: params.customerId } : {}),
            },
          }
        );
        setReportData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            errorMessages.FAILED_TO_FETCH_ORDER_REPORT
        );
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, error, reportData, fetchOrderReport };
};

export const useGetProductReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<ProductReportResponse | null>(
    null
  );

  const fetchProductReport = useCallback(
    async (params: { startDate: string; endDate: string }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.PRODUCT_REPORT_API,
          {
            params: {
              startDate: params.startDate,
              endDate: params.endDate,
            },
          }
        );
        setReportData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            errorMessages.FAILED_TO_FETCH_PRODUCT_REPORT
        );
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, error, reportData, fetchProductReport };
};

export const useGetCustomerReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<CustomerReportResponse | null>(
    null
  );

  const fetchCustomerReport = useCallback(
    async (params: { startDate: string; endDate: string }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.CUSTOMER_REPORT_API,
          {
            params: {
              startDate: params.startDate,
              endDate: params.endDate,
            },
          }
        );
        setReportData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            errorMessages.FAILED_TO_FETCH_CUSTOMER_REPORT
        );
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, error, reportData, fetchCustomerReport };
};

export const useGetCustomerOutstandingReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] =
    useState<CustomerOutstandingReportResponse | null>(null);

  const fetchCustomerOutstandingReport = useCallback(
    async (params?: { customerId?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.CUSTOMER_OUTSTANDING_REPORT_API,
          {
            params: {
              ...(params?.customerId ? { customerId: params.customerId } : {}),
            },
          }
        );
        setReportData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message ||
            errorMessages.FAILED_TO_FETCH_CUSTOMER_OUTSTANDING_REPORT
        );
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, error, reportData, fetchCustomerOutstandingReport };
};

export const useGetPaymentReport = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [reportData, setReportData] = useState<PaymentReportResponse | null>(
    null
  );

  const fetchPaymentReport = useCallback(
    async (params: {
      startDate: string;
      endDate: string;
      customerId?: string;
    }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.PAYMENT_REPORT_API,
          {
            params: {
              startDate: params.startDate,
              endDate: params.endDate,
              ...(params.customerId ? { customerId: params.customerId } : {}),
            },
          }
        );
        setReportData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 'Failed to fetch payment report.'
        );
        setReportData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, error, reportData, fetchPaymentReport };
};

export const useGetDashboardSummary = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [summaryData, setSummaryData] =
    useState<DashboardSummaryResponse | null>(null);

  const fetchDashboardSummary = useCallback(
    async (params?: { startDate?: string; endDate?: string }) => {
      setLoading(true);
      setError(null);
      try {
        const response = await axiosTokenInstance.get(
          apiUrls.DASHBOARD_SUMMARY_API,
          {
            params: {
              ...(params?.startDate ? { startDate: params.startDate } : {}),
              ...(params?.endDate ? { endDate: params.endDate } : {}),
            },
          }
        );
        setSummaryData(response.data);
      } catch (err: any) {
        setError(
          err.response?.data?.message || 'Failed to fetch dashboard summary.'
        );
        setSummaryData(null);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  return { loading, error, summaryData, fetchDashboardSummary };
};
